import { useCallback, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import { SORT_DIRECTION, AUCTION_STATES } from "../../models";

import AuctionNavigation from "../auctionNavigation";
import Carousel from "../carousel";
import ConnectWallet from "../connectWallet";
import Viewer from "../viewer";
import WalletSelector from "../walletSelector";

import auctionService from "../../services/auction";
import { W3Context } from "../../services/context";
import { FiltersContext } from "../../services/context/filters";

import clock from "../../assets/clock.png";
import clockWhite from "../../assets/clock_white.png";
import fire from "../../assets/fire.png";
import hammer from "../../assets/hammer.png";
import hammerBlack from "../../assets/hammer_black.png";

import { GoTriangleDown, GoTriangleUp } from "react-icons/go";

import "./homeStyles.css";

const { REACT_APP_AUCTIONS_PER_PAGE = 1, REACT_APP_IMMINENT_LIMIT = 10 } =
  process.env;

const auctionsPerPage = parseInt(REACT_APP_AUCTIONS_PER_PAGE);
const imminentLimit = parseInt(REACT_APP_IMMINENT_LIMIT);

const Home = ({ onClickConnectWallet }) => {
  const { filters } = useContext(FiltersContext);
  const { active, account /*library*/ } = useContext(W3Context);

  const [displayModal, setDisplayModal] = useState(false);
  const [priceOrder, setPriceOrder] = useState(0);
  const [endTimeOrder, setEndTimeOrder] = useState(0);
  const [hottestAuctions, setHottestAuctions] = useState([]);
  const [imminentAuctions, setImminentAuctions] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [skipAuctions, setSkipAuctions] = useState(0);
  const [sort, setSort] = useState("");
  const [shouldRefreshAuctions, setShouldRefreshAuctions] = useState(false);
  const [showViewMoreButton, setShowViewMoreButton] = useState(true);

  const priceOrderClick = async () => {
    setEndTimeOrder(0);
    const field = "currentValue";
    let order = "";

    if (priceOrder === 0) {
      setPriceOrder(1);
      order = field + "," + SORT_DIRECTION.ASC;
    } else if (priceOrder === 1) {
      setPriceOrder(-1);
      order = field + "," + SORT_DIRECTION.DESC;
    } else {
      setPriceOrder(0);
      order = "";
    }

    setSort(order);

    const allAuctions = await auctionService.getAll(
      {
        skip: 0,
        limit: auctionsPerPage,
        sort: order,
        state: AUCTION_STATES.ACTIVE,
      },
      { address: account }
    );

    setAuctions(allAuctions);
  };

  const endTimeOrderClick = async () => {
    setPriceOrder(0);
    const field = "expirationDate";
    let order = "";

    if (endTimeOrder === 0) {
      setEndTimeOrder(1);
      order = field + "," + SORT_DIRECTION.ASC;
    } else if (endTimeOrder === 1) {
      setEndTimeOrder(-1);
      order = field + "," + SORT_DIRECTION.DESC;
    } else {
      setEndTimeOrder(0);
      order = "";
    }

    setSort(order);

    const allAuctions = await auctionService.getAll(
      {
        skip: 0,
        limit: auctionsPerPage,
        sort: order,
        state: AUCTION_STATES.ACTIVE,
      },
      { address: account }
    );

    setAuctions(allAuctions);
  };

  const getAuctions = useCallback(async () => {
    try {
      if (account) {
        if (Object.keys(filters).length > 0) {
          setShouldRefreshAuctions(true);
        }

        const hottest = await auctionService.getAll(
          {
            // type: AUCTION_TYPES.PREMIUM,
            isHottest: true,
            sort: "_id," + SORT_DIRECTION.ASC,
            state: AUCTION_STATES.ACTIVE,
            ...filters,
          },
          { address: account }
        );
        setHottestAuctions(hottest);

        const imminent = await auctionService.getAll(
          {
            skip: 0,
            limit: imminentLimit,
            sort: "_id," + SORT_DIRECTION.DESC,
            state: AUCTION_STATES.ACTIVE,
            ...filters,
          },
          { address: account }
        );
        setImminentAuctions(imminent);

        const allAuctions = await auctionService.getAll(
          {
            skip: 0,
            limit: auctionsPerPage,
            state: AUCTION_STATES.ACTIVE,
            ...filters,
          },
          { address: account }
        );
        setAuctions(allAuctions);
      }
    } catch (error) {
      console.log(error);
    }
  }, [account, filters]);

  useEffect(() => {
    getAuctions();
  }, [getAuctions]);

  const onClickViewMore = async () => {
    try {
      if (shouldRefreshAuctions) {
        const allAuctions = await auctionService.getAll(
          {
            skip: 0,
            limit: auctionsPerPage,
            sort: sort,
            state: AUCTION_STATES.ACTIVE,
          },
          { address: account }
        );
        setAuctions(allAuctions);
        setShouldRefreshAuctions(false);
        return;
      }

      const toSkip = skipAuctions + 1;
      const returnedAuctions = await auctionService.getAll(
        {
          skip: toSkip,
          limit: auctionsPerPage,
          sort: sort,
          state: AUCTION_STATES.ACTIVE,
        },
        { address: account }
      );

      if (
        returnedAuctions.length === 0 ||
        returnedAuctions.length < auctionsPerPage
      ) {
        setShowViewMoreButton(false);
      }

      setAuctions([...auctions, ...returnedAuctions]);
      setSkipAuctions(toSkip);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Container className="auctions">
      <Row className="title mb-2">
        <p>
          <img src={hammer} alt="auctions" />
          <span className="purple">GEOPOLY</span>
          NFT Auctions
        </p>
      </Row>
      <Row className="description justify-content-center text-center mb-3">
        <p>
          Welcome to Geopoly Auctions! <br />
          View all NFT properties that are up for auctions, place your bid, or
          put your NFT for sale and track them live.
        </p>
      </Row>
      <AuctionNavigation onClickConnectWallet={onClickConnectWallet} />
      <Row className="mt-5">
        <Col>
          {!active ? (
            <>
              <ConnectWallet setDisplayModal={setDisplayModal} />
              <WalletSelector
                displayModal={displayModal}
                setDisplayModal={setDisplayModal}
              />
            </>
          ) : (
            <>
              {hottestAuctions.length === 0 &&
              imminentAuctions.length === 0 &&
              auctions.length === 0 &&
              Object.keys(filters).length > 0 ? (
                <Container className="no-auctions">
                  <Row className="d-flex justify-content-center text-center">
                    <Col>There are no auctions that apply to these filters</Col>
                  </Row>
                </Container>
              ) : (
                <>
                  {hottestAuctions.length > 0 && (
                    <Container className="searching-container px-0">
                      <Row className="d-flex justify-content-center">
                        <Col
                          xs={12}
                          className="text-center mx-auto justify-content-center"
                        >
                          <div className="divider-purple align-items-center">
                            <img src={fire} alt="hottest_1" />
                            <span className="mx-3">Hottest</span>
                            <img src={fire} alt="hottest_2" />
                          </div>
                        </Col>
                        <Col xs={12}>
                          <Carousel NFTs={hottestAuctions} background={true} />
                        </Col>
                      </Row>
                    </Container>
                  )}
                  {imminentAuctions.length > 0 && (
                    <Container className="searching-container px-0">
                      <Row className="d-flex justify-content-center mt-5">
                        <Col
                          xs={12}
                          className="text-center mx-auto justify-content-center"
                        >
                          <div className="divider-purple align-items-center">
                            <img src={clock} alt="clock_1" />
                            <span className="mx-3">Imminent</span>
                            <img src={clock} alt="clock_2" />
                          </div>
                        </Col>
                        <Col xs={12}>
                          <Carousel NFTs={imminentAuctions} background={true} />
                        </Col>
                      </Row>
                    </Container>
                  )}
                  {auctions.length > 0 && (
                    <Container className="searching-container px-0">
                      <Row className="d-flex justify-content-center mt-5">
                        <Col
                          xs={12}
                          className="text-center justify-content-center mx-auto"
                        >
                          <div className="divider-purple align-items-center">
                            <img src={hammerBlack} alt="hammer_1" />
                            <span className="mx-3">Auctions</span>
                            <img src={hammerBlack} alt="hammer_2" />
                          </div>
                        </Col>
                        <Col
                          xs={5}
                          md={3}
                          lg={2}
                          className="text-center justify-content-center mx-auto"
                        >
                          <Row className="order-buttons align-items-center">
                            <Col xs={6}>
                              <button
                                className="dollar-button"
                                onClick={priceOrderClick}
                              >
                                <span>$</span>
                                {priceOrder !== 0 && (
                                  <>
                                    {priceOrder === 1 ? (
                                      <GoTriangleUp className="arrow" />
                                    ) : (
                                      <GoTriangleDown className="arrow" />
                                    )}
                                  </>
                                )}
                              </button>
                            </Col>
                            <Col xs={6}>
                              <button
                                className="clock-button"
                                onClick={endTimeOrderClick}
                              >
                                <img
                                  className="mb-1"
                                  src={clockWhite}
                                  alt="clock-white"
                                />
                                {endTimeOrder !== 0 && (
                                  <>
                                    {endTimeOrder === 1 ? (
                                      <GoTriangleUp className="arrow" />
                                    ) : (
                                      <GoTriangleDown className="arrow" />
                                    )}
                                  </>
                                )}
                              </button>
                            </Col>
                          </Row>
                        </Col>
                        <Col xs={12}>
                          <Viewer NFTs={auctions} background={false} />
                        </Col>
                        {showViewMoreButton && (
                          <Col xs={12} className="text-center">
                            <button
                              onClick={onClickViewMore}
                              className="purple-button mx-auto"
                            >
                              VIEW MORE
                            </button>
                          </Col>
                        )}
                      </Row>
                    </Container>
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Home;
